/**
 * Sticky live-call wayfinding rail for the intake call guide (Phase 3): lists the
 * visible sections in order, highlights the section currently in view, and jumps
 * to a section on click. The section list is a read-only navigation aid — it
 * captures nothing (D1). Below the rail sits the general (whole-call) scratch pad,
 * which — unlike the per-section scratch — is captured; it is bound to the intake
 * encounter's scratch extension so it persists and restores on edit.
 * Entries respect pediatric gating because the caller passes only the sections it
 * renders, so the rail and the cards always agree (D3).
 * @agentic connections
 */

import {
  Box,
  Divider,
  Group,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { IconNote } from "@tabler/icons-react";

import type { NoteSection } from "../note-sections";

/**
 * Props for the intake guide wayfinding rail.
 * @agentic connections
 */
export type IntakeGuideWayfindingProps = {
  /** The sections currently rendered in the guide, in display order. */
  sections: Pick<NoteSection, "id" | "label" | "audience">[];
  /** Id of the section currently in view (highlighted). */
  activeSectionId?: string;
  /**
   * Ids of sections that already have notes (non-empty per-section scratch).
   * Rendered with a non-color has-notes indicator so notes-progress is visible
   * at a glance (§8.1.3; WCAG 1.4.1 — the cue is a glyph, not color alone). The
   * current-section highlight and this cue are independent and can co-occur.
   */
  sectionsWithNotes?: Set<string>;
  /** Jump to a section's card. */
  onJump: (sectionId: string) => void;
  /**
   * General (whole-call) scratch notes, shown only for intake encounters. When
   * omitted, the general scratch pad is hidden.
   */
  generalScratch?: string;
  /** Called when the general scratch note changes. */
  onGeneralScratchChange?: (value: string) => void;
};

/**
 * Persistent, glanceable section rail. Sticky so it stays visible without
 * scrolling; the active section is highlighted and every entry jumps to its card.
 * @agentic connections
 */
export const IntakeGuideWayfinding = ({
  sections,
  activeSectionId,
  sectionsWithNotes,
  onJump,
  generalScratch,
  onGeneralScratchChange,
}: IntakeGuideWayfindingProps) => {
  const showGeneralScratch = onGeneralScratchChange !== undefined;

  return (
    <Box
      data-testid="guide-wayfinding"
      style={{
        position: "sticky",
        top: "var(--mantine-spacing-md)",
        alignSelf: "flex-start",
        flex: "0 0 260px",
        maxWidth: 260,
        maxHeight: "calc(100vh - var(--mantine-spacing-xl))",
        display: "flex",
        flexDirection: "column",
        gap: "var(--mantine-spacing-sm)",
      }}
    >
      <Box
        component="nav"
        aria-label="Guide sections"
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <Stack gap={2}>
          {sections.map((section) => {
            const active = section.id === activeSectionId;
            const hasNotes = sectionsWithNotes?.has(section.id) ?? false;
            return (
              <UnstyledButton
                key={section.id}
                onClick={() => onJump(section.id)}
                data-testid={`wayfinding-entry-${section.id}`}
                aria-current={active ? "true" : undefined}
                style={{
                  padding: "6px 10px",
                  borderRadius: "var(--mantine-radius-sm)",
                  borderLeft: active
                    ? "3px solid var(--mantine-primary-color-filled)"
                    : "3px solid transparent",
                  background: active
                    ? "var(--mantine-primary-color-light)"
                    : "transparent",
                }}
              >
                <Group gap={6} wrap="nowrap" justify="space-between">
                  <Text
                    size="xs"
                    fw={active ? 700 : 500}
                    c={active ? undefined : "gray.7"}
                    style={
                      active
                        ? { color: "var(--mantine-primary-color-light-color)" }
                        : undefined
                    }
                  >
                    {section.label}
                  </Text>
                  {/* Non-color has-notes cue: a note glyph (shape, not color)
                  distinguishes sections that already have notes, independent of
                  the current-section highlight (§8.1.3/§8.1.3a, WCAG 1.4.1). */}
                  {hasNotes && (
                    <IconNote
                      size={14}
                      aria-label="has notes"
                      data-testid={`wayfinding-notes-indicator-${section.id}`}
                      style={{
                        flex: "0 0 auto",
                        color: "var(--mantine-color-gray-6)",
                      }}
                    />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      </Box>

      {showGeneralScratch && (
        <>
          <Divider />

          <Textarea
            label="General scratch notes"
            placeholder="Working notes for the call — saved to this encounter, but not shown in the member's health record or the note copy/paste"
            value={generalScratch ?? ""}
            onChange={(e) => onGeneralScratchChange(e.currentTarget.value)}
            autosize
            minRows={5}
            maxRows={8}
            data-testid="general-scratch-input"
          />
        </>
      )}
    </Box>
  );
};
